/** The two instants every helper here needs; a seat from the API has both. */
export interface SlotTimes {
  /** UTC instant, ISO 8601. */
  startsAt: string;
  /** UTC instant, ISO 8601. */
  endsAt: string;
}

/** Formatting locale for the public page; the page itself is English for the MVP. */
const LOCALE = 'en-GB';

const FALLBACK_ZONES = [
  'Europe/London',
  'Europe/Berlin',
  'Europe/Sarajevo',
  'America/New_York',
  'America/Chicago',
  'America/Los_Angeles',
  'Asia/Kolkata',
  'Asia/Kathmandu',
  'Asia/Singapore',
  'Australia/Sydney',
  'UTC',
];

export const getBrowserTimeZone = (): string =>
  Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';

/** Every IANA zone the runtime knows, or a short list where it cannot say. */
export const listTimeZones = (): string[] => {
  const intl = Intl as unknown as {
    supportedValuesOf?: (key: string) => string[];
  };
  const zones = intl.supportedValuesOf?.('timeZone');
  return zones && zones.length > 0 ? zones : FALLBACK_ZONES;
};

const partsOf = (
  iso: string,
  timeZone: string,
  options: Intl.DateTimeFormatOptions
): Record<string, string> => {
  const parts = new Intl.DateTimeFormat(LOCALE, {
    timeZone,
    ...options,
  }).formatToParts(new Date(iso));
  return Object.fromEntries(
    parts.filter(p => p.type !== 'literal').map(p => [p.type, p.value])
  );
};

const zoneAbbreviationAt = (
  formatter: Intl.DateTimeFormat,
  atIso: string
): string | undefined =>
  formatter.formatToParts(new Date(atIso)).find(p => p.type === 'timeZoneName')
    ?.value;

const zoneAbbreviationThroughout = (
  timeZone: string,
  atIsos: readonly string[],
  locale: string
): string | undefined => {
  if (atIsos.length === 0) {
    return undefined;
  }
  const formatter = new Intl.DateTimeFormat(locale, {
    timeZone,
    timeZoneName: 'short',
  });
  const sorted = [...atIsos].sort();
  const first = zoneAbbreviationAt(formatter, sorted[0]);
  const last = zoneAbbreviationAt(formatter, sorted[sorted.length - 1]);
  return first === last ? first : undefined;
};

/**
 * Cities whose IANA identifier still carries an old spelling. Browsers list
 * `Asia/Calcutta`, not `Asia/Kolkata`, so without this a candidate in India
 * searching "Kolkata" finds nothing. The identifier itself is left alone:
 * both spellings are valid input to Intl, only the label changes.
 */
const CITY_ALIASES: Record<string, string> = {
  Calcutta: 'Kolkata',
  Katmandu: 'Kathmandu',
  Rangoon: 'Yangon',
  Saigon: 'Ho Chi Minh City',
  Kiev: 'Kyiv',
  Godthab: 'Nuuk',
  Faeroe: 'Faroe',
  Enderbury: 'Kanton',
  Ponape: 'Pohnpei',
  Truk: 'Chuuk',
};

/**
 * "Sarajevo (CEST)": the zone's city and its abbreviation across the given
 * instants, or the city alone when a clock change falls between them. No
 * single locale abbreviates every zone (en-GB knows BST but not EDT, en-US
 * the reverse), so a named abbreviation from either wins over a bare GMT
 * offset.
 */
export const timeZoneLabel = (
  timeZone: string,
  atIsos: readonly string[]
): string => {
  const rawCity = timeZone.split('/').pop() ?? timeZone;
  const city = (CITY_ALIASES[rawCity] ?? rawCity).replace(/_/g, ' ');
  const candidates = [LOCALE, 'en-US'].map(locale =>
    zoneAbbreviationThroughout(timeZone, atIsos, locale)
  );
  const abbreviation =
    candidates.find(name => name && !/^(GMT|UTC)/.test(name)) ??
    candidates.find(Boolean);
  return abbreviation ? `${city} (${abbreviation})` : city;
};

/** Calendar day an instant falls on in a zone, as "YYYY-MM-DD". */
export const zonedDayKey = (iso: string, timeZone: string): string => {
  const { year, month, day } = partsOf(iso, timeZone, {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  });
  return `${year}-${month}-${day}`;
};

/** Local-midnight Date for a day key, which is what the calendar widget compares. */
export const dayKeyToDate = (key: string): Date => {
  const [year, month, day] = key.split('-').map(Number);
  return new Date(year, month - 1, day);
};

export const dateToDayKey = (date: Date): string =>
  [
    date.getFullYear(),
    String(date.getMonth() + 1).padStart(2, '0'),
    String(date.getDate()).padStart(2, '0'),
  ].join('-');

/** "09:00", 24-hour clock in the zone. */
export const formatTime = (iso: string, timeZone: string): string => {
  const { hour, minute } = partsOf(iso, timeZone, {
    hour: '2-digit',
    minute: '2-digit',
    hourCycle: 'h23',
  });
  return `${hour}:${minute}`;
};

/** "09:00 – 09:45" */
export const formatSlotRange = (slot: SlotTimes, timeZone: string): string =>
  `${formatTime(slot.startsAt, timeZone)} – ${formatTime(slot.endsAt, timeZone)}`;

/** "Wednesday September 9", as the prototype labels the chosen day. */
export const formatDayFull = (iso: string, timeZone: string): string => {
  const { weekday, month, day } = partsOf(iso, timeZone, {
    weekday: 'long',
    month: 'long',
    day: 'numeric',
  });
  return `${weekday} ${month} ${day}`;
};

/** "7 September" */
export const formatExpiry = (iso: string, timeZone: string): string => {
  const { month, day } = partsOf(iso, timeZone, {
    month: 'long',
    day: 'numeric',
  });
  return `${day} ${month}`;
};

export interface OfferDay<T extends SlotTimes = SlotTimes> {
  key: string;
  date: Date;
  slots: T[];
}

/** Slots grouped by the day they fall on in the zone, both levels sorted. */
export const groupSlotsByDay = <T extends SlotTimes>(
  slots: T[],
  timeZone: string
): OfferDay<T>[] => {
  const byKey = new Map<string, T[]>();
  [...slots]
    .sort((a, b) => a.startsAt.localeCompare(b.startsAt))
    .forEach(s => {
      const key = zonedDayKey(s.startsAt, timeZone);
      byKey.set(key, [...(byKey.get(key) ?? []), s]);
    });
  return [...byKey.entries()]
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([key, daySlots]) => ({
      key,
      date: dayKeyToDate(key),
      slots: daySlots,
    }));
};
